import type { AppConfigKeys, KvKeys } from '@rialtic/types'
import { featureFlags, parseAppConfig } from '@rialtic/types'
import { acceptHMRUpdate, defineStore } from 'pinia'

// @todo(wfl-6352): These types shouldn't be necessary but the `featureFlags` const is not strictly typed
// using the expected keys from the `AppConfigKeys` enum.
type FeatureFlagKey = keyof typeof featureFlags
type FeatureFlags = Record<FeatureFlagKey, boolean>
type AppConfigState = FeatureFlags & {
  isInitialized: boolean
}

let abortController: AbortController | null = null

export const useAppConfiguration = defineStore('app-config', {
  state: (): AppConfigState => ({ ...featureFlags, isInitialized: false }),

  actions: {
    async getKey(key: AppConfigKeys) {
      const { $datadog } = useNuxtApp()
      const api = useWorkersApi()

      try {
        // @ts-expect-error - figure out why this triggers an `excessive stack depth` type error (sometimes)
        const isSet = (await api.fetchPublic(`/app-config/${key}`)) as
          | string
          | null

        this.$patch({
          [key]: Boolean(isSet),
        })
      } catch (error) {
        $datadog.addError(error, {
          function: 'stores/appConfig:getKey',
        })
      }

      return this.$state
    },

    async getAll() {
      if (this.isInitialized) return this.$state
      abortController?.abort()
      abortController = new AbortController()
      const { signal } = abortController

      const { $datadog } = useNuxtApp()
      const api = useWorkersApi()

      try {
        const keys: KvKeys[] = await api.fetchPublic('/app-config', { signal })

        this.$patch({
          ...(parseAppConfig(keys) as FeatureFlags),
          isInitialized: true,
        })
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          $datadog.addError(error, {
            function: 'stores/appConfig:getAll',
          })
        }
      } finally {
        abortController = null
      }

      return this.$state
    },

    async toggleKey(key: AppConfigKeys) {
      return this.updateAppConfigKey(key, !this.$state[key as FeatureFlagKey])
    },

    async updateAppConfigKey(key: AppConfigKeys, isSet: boolean) {
      const { $datadog } = useNuxtApp()
      const api = useWorkersApi()

      const prevVal = this.$state[key as FeatureFlagKey]

      this.$patch({
        [key]: isSet,
      })

      try {
        if (isSet) {
          return api.fetch(`/admin/app-config/${key}`, {
            method: 'PUT',
            body: {
              value: 'true',
            },
          })
        }
        return api.fetch(`/admin/app-config/${key}`, {
          method: 'DELETE',
        })
      } catch (error) {
        this.$patch({
          [key]: prevVal,
        })

        $datadog.addError(error, {
          function: 'stores/appConfig:updateAppConfigKey',
        })

        throw error
      }
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useAppConfiguration, import.meta.hot))
